import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useTheme } from "src/context/ThemeContext";
import { NewSidebarProps, NavigationItem } from "../types";
import SidebarItem from "../SidebarItem";
import { BalanceWidget } from "../components";
import {
  createMainNavigationData,
  createSettingsNavigationData,
} from "../utils/navigationMapper";
import "../../../../styles/css/sidebar-layout.css";

const NewSidebar: React.FC<NewSidebarProps> = ({
  isMinimized = false,
  navigationItems,
  balance,
  onToggleMinimize,
  className = "",
}) => {
  const location = useLocation();
  const { t } = useTranslation();
  const { theme } = useTheme();
  const [activeItemId, setActiveItemId] = useState<string | null>(null);
  const [activeParentId, setActiveParentId] = useState<string | null>(null);
  const [expandedItems, setExpandedItems] = useState<string[]>([]);

  // Multi-level navigation state
  const [currentLevel, setCurrentLevel] = useState<"main" | "settings">("main");
  const [navigationHistory, setNavigationHistory] = useState<string[]>([
    "main",
  ]);
  const [manuallyNavigatedToMain, setManuallyNavigatedToMain] = useState(false);

  // Helper function to generate balance tooltip content
  const getBalanceTooltipContent = (balance: any) => {
    if (typeof balance === "object" && balance.amount) {
      return `Balance: ${
        balance.currency || ""
      } ${balance.amount.toLocaleString()}`;
    }
    return "View Balance";
  };

  // Update active item based on current route
  useEffect(() => {
    const findActiveItem = (
      items: NavigationItem[],
      parentId: string | null = null
    ): { activeId: string | null; parentId: string | null } => {
      for (const item of items) {
        // Skip items with # paths for direct matching
        if (item.path && item.path !== "#") {
          // Exact match first
          if (location.pathname === item.path) {
            return { activeId: item.id, parentId };
          }
          // Then check if path starts with item.path (but avoid false positives)
          if (
            item.path.length > 1 &&
            location.pathname.startsWith(item.path + "/")
          ) {
            return { activeId: item.id, parentId };
          }
        }

        // Check children
        if (item.children && item.children.length > 0) {
          const childResult = findActiveItem(item.children, item.id);
          if (childResult.activeId) {
            return childResult;
          }
        }
      }
      return { activeId: null, parentId: null };
    };

    // Check if we're on a settings path
    if (location.pathname.startsWith("/setting")) {
      const settingsItems = createSettingsNavigationData(t);
      const result = findActiveItem(settingsItems);

      if (result.activeId || result.parentId) {
        // Only auto-switch to settings level if user hasn't manually chosen to stay in main
        if (currentLevel !== "settings" && !manuallyNavigatedToMain) {
          setCurrentLevel("settings");
          setNavigationHistory(["main", "settings"]);
        }
        setActiveItemId(result.activeId);
        setActiveParentId(result.parentId);

        // Auto-expand parent if child is active
        if (result.parentId) {
          setExpandedItems((prev) =>
            prev.includes(result.parentId!) ? prev : [...prev, result.parentId!]
          );
        }
        return;
      }
    } else {
      // Reset manual navigation flag when leaving settings paths
      if (manuallyNavigatedToMain) {
        setManuallyNavigatedToMain(false);
      }
    }

    // Check main navigation for active items
    const result = findActiveItem(navigationItems);
    setActiveItemId(result.activeId);
    setActiveParentId(result.parentId);

    // Auto-expand parent if child is active
    if (result.parentId) {
      setExpandedItems((prev) =>
        prev.includes(result.parentId!) ? prev : [...prev, result.parentId!]
      );
    }
  }, [
    location.pathname,
    navigationItems,
    currentLevel,
    manuallyNavigatedToMain,
    t,
  ]);

  const handleItemClick = (item: NavigationItem) => {
    // Handle navigation to Settings level
    if (item.id === "settings") {
      setCurrentLevel("settings");
      setNavigationHistory(["main", "settings"]);
      return;
    }

    // Only handle expansion for parent items - don't change active state
    if (item.children && item.children.length > 0) {
      setExpandedItems((prev) =>
        prev.includes(item.id)
          ? prev.filter((id) => id !== item.id)
          : [...prev, item.id]
      );
    }

    // Call custom onClick handlers (like logout)
    if (item.onClick) {
      item.onClick();
    }

    // Note: Active state is controlled by useEffect watching location.pathname
    // Navigation is handled by React Router when clicking Links in SidebarItem
  };

  const handleBackNavigation = () => {
    if (navigationHistory.length > 1) {
      const newHistory = [...navigationHistory];
      newHistory.pop(); // Remove current level
      const previousLevel = newHistory[newHistory.length - 1];
      setNavigationHistory(newHistory);
      setCurrentLevel(previousLevel as "main" | "settings");

      // Set flag to indicate user manually navigated back to main
      if (previousLevel === "main") {
        setManuallyNavigatedToMain(true);
      }
    }
  };

  // Get current navigation items based on level
  const getCurrentNavigationItems = () => {
    switch (currentLevel) {
      case "settings":
        return createSettingsNavigationData(t);
      case "main":
      default:
        return navigationItems;
    }
  };

  // Get current navigation items and split them
  const currentNavigationItems = getCurrentNavigationItems();
  const mainItems = currentNavigationItems.filter(
    (item) => !["help", "logout"].includes(item.id)
  );
  const bottomItems =
    currentLevel === "main"
      ? navigationItems.filter((item) => ["help", "logout"].includes(item.id))
      : []; // No bottom items in settings level

  return (
    <aside
      className={`tw-fixed tw-top-16 tw-bottom-0 tw-left-0 tw-z-[100] tw-flex tw-flex-col tw-border-r ${
        theme === "light"
          ? "tw-border-gray-200 tw-bg-white"
          : "tw-border-slate-600 tw-bg-slate-900"
      } tw-transition-width tw-duration-300 tw-ease-in-out ${
        isMinimized ? "sidebar-minimized" : "sidebar-expanded"
      } ${className}`}
      role="navigation"
      aria-label="Main navigation"
    >
      <div className="tw-flex tw-h-0 tw-flex-1 tw-flex-col tw-overflow-y-auto tw-overflow-x-hidden">
        {/* Sliding container for navigation levels */}
        <div
          className="tw-flex tw-h-full tw-transition-transform tw-duration-300 tw-ease-in-out tw-overflow-x-hidden"
          style={{
            transform: `translateX(${currentLevel === "main" ? "0%" : "-50%"})`,
            width: "200%",
          }}
        >
          {/* Main navigation level */}
          <div className="tw-flex tw-w-1/2 tw-flex-col tw-overflow-y-auto">
            <nav className="tw-flex-1 tw-space-y-1 tw-px-3 tw-py-4">
              {/* Main level items */}
              {navigationItems
                .filter((item) => !["help", "logout"].includes(item.id))
                .map((item) => (
                  <SidebarItem
                    key={item.id}
                    item={{
                      ...item,
                      isActive:
                        activeItemId === item.id || activeParentId === item.id,
                    }}
                    isMinimized={isMinimized}
                    onItemClick={handleItemClick}
                    isExpanded={expandedItems.includes(item.id)}
                    activeItemId={activeItemId}
                    activeParentId={activeParentId}
                    depth={0}
                    expandedItems={expandedItems}
                  />
                ))}
            </nav>
          </div>

          {/* Settings navigation level */}
          <div className="tw-flex tw-w-1/2 tw-flex-col tw-overflow-y-auto">
            <nav className="tw-flex-1 tw-space-y-1 tw-px-3 tw-py-4">
              {/* Back button and header for settings level */}
              {!isMinimized ? (
                <div className="tw-mb-1">
                  {/* Settings Header with integrated back button */}
                  <div
                    className={`tw-flex tw-items-center tw-gap-3 tw-px-2 tw-border-b ${
                      theme === "light"
                        ? "tw-border-gray-200"
                        : "tw-border-slate-600"
                    } tw-pb-3 tw-cursor-pointer`}
                    onClick={handleBackNavigation}
                  >
                    <button
                      className={`tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-lg ${
                        theme === "light"
                          ? "tw-bg-gray-50 hover:tw-bg-gray-100"
                          : "tw-bg-slate-700 hover:tw-bg-slate-600"
                      } tw-transition-all tw-duration-200 hover:tw-shadow-sm`}
                      title="Back to main menu"
                    >
                      <span
                        className={`material-symbols-outlined tw-text-lg ${
                          theme === "light"
                            ? "tw-text-gray-600"
                            : "tw-text-slate-400"
                        }`}
                      >
                        chevron_left
                      </span>
                    </button>
                    <div className="tw-flex tw-flex-col">
                      <h2
                        className={`tw-text-sm tw-font-semibold ${
                          theme === "light"
                            ? "tw-text-gray-900"
                            : "tw-text-slate-50"
                        }`}
                      >
                        {t("SideBar.MenuItem.Settings")}
                      </h2>
                    </div>
                  </div>
                </div>
              ) : (
                /* Minimized state - show just a compact back button */
                <div className="tw-mb-4 tw-flex tw-justify-center">
                  <button
                    onClick={handleBackNavigation}
                    className={`tw-flex tw-h-10 tw-w-10 tw-items-center tw-justify-center tw-rounded-lg ${
                      theme === "light"
                        ? "tw-bg-gray-50 hover:tw-bg-gray-100"
                        : "tw-bg-slate-700 hover:tw-bg-slate-600"
                    } tw-transition-all tw-duration-200 hover:tw-shadow-sm`}
                    data-sidebar-tooltip="Back to main menu"
                  >
                    <span
                      className={`material-symbols-outlined ${
                        theme === "light"
                          ? "tw-text-gray-600"
                          : "tw-text-slate-400"
                      }`}
                    >
                      arrow_back
                    </span>
                  </button>
                </div>
              )}

              {/* Settings level items */}
              {createSettingsNavigationData(t).map((item) => (
                <SidebarItem
                  key={item.id}
                  item={{
                    ...item,
                    isActive:
                      activeItemId === item.id || activeParentId === item.id,
                  }}
                  isMinimized={isMinimized}
                  onItemClick={handleItemClick}
                  isExpanded={expandedItems.includes(item.id)}
                  activeItemId={activeItemId}
                  activeParentId={activeParentId}
                  depth={0}
                  expandedItems={expandedItems}
                />
              ))}
            </nav>
          </div>
        </div>
      </div>

      {/* Bottom section with balance widget and logout - only show on main level */}
      {currentLevel === "main" && (
        <div className="tw-px-3 tw-py-4 tw-mt-auto">
          {/* Balance Widget for expanded state */}
          {balance && !isMinimized && (
            <BalanceWidget balance={balance} isMinimized={isMinimized} />
          )}

          {/* Minimized balance widget button */}
          {balance && isMinimized && (
            <div className="tw-mb-4">
              <div className="tw-flex tw-justify-center">
                <button
                  className={`tw-flex tw-h-10 tw-w-10 tw-items-center tw-justify-center tw-rounded-lg ${
                    theme === "light"
                      ? "tw-bg-primary/10 tw-text-primary hover:tw-bg-primary/20"
                      : "tw-bg-slate-600 tw-text-slate-300 hover:tw-bg-slate-500"
                  }`}
                  data-sidebar-tooltip={getBalanceTooltipContent(balance)}
                >
                  <span className="material-symbols-outlined">
                    account_balance_wallet
                  </span>
                </button>
              </div>
            </div>
          )}

          {/* Logout section */}
          {bottomItems.map((item) => (
            <SidebarItem
              key={item.id}
              item={{
                ...item,
                isActive: activeItemId === item.id,
              }}
              isMinimized={isMinimized}
              onItemClick={handleItemClick}
              isExpanded={expandedItems.includes(item.id)}
              activeItemId={activeItemId}
              activeParentId={activeParentId}
              depth={0}
              expandedItems={expandedItems}
            />
          ))}
        </div>
      )}

      {/* Toggle button positioned like HTML design */}
      <button
        onClick={onToggleMinimize}
        className={`tw-absolute tw--right-3 tw-top-2 tw-flex tw-h-6 tw-w-6 tw-items-center tw-justify-center tw-rounded-full tw-border ${
          theme === "light"
            ? "tw-border-gray-300 tw-bg-white hover:tw-bg-gray-100"
            : "tw-border-slate-600 tw-bg-slate-800 hover:tw-bg-slate-600"
        } tw-shadow-sm tw-transition-all tw-duration-300 hover:tw-scale-110 active:tw-scale-95`}
        title={isMinimized ? "Expand sidebar" : "Minimize sidebar"}
      >
        <span
          className={`material-symbols-outlined tw-text-base tw-transition-transform tw-duration-300 ${
            isMinimized ? "tw-rotate-180" : ""
          }`}
        >
          chevron_left
        </span>
      </button>
    </aside>
  );
};

export default NewSidebar;
